import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { PropsWithChildren } from 'react';
import { InfoOutlined, DragIndicator } from '@mui/icons-material';
import { formatLabel } from 'ui-component/RecordView/PropertiesPanel/utils';
import { FieldMenu } from 'ui-component/RecordView/PropertiesPanel/AllPanelContainer/FieldMenu';

export type PanelFieldSectionInlineProps = {
    title: string;
    isFocused?: boolean;
    isRequired?: boolean;
    dataType?: string;
    error?: string;
    disabled?: boolean;
    isRecordField?: boolean;
    centerContent?: boolean;
    isDraggable?: boolean;
    isDragging?: boolean;
    showFieldMenu?: boolean;
    rightHandPanelWidth?: number;
    isRecordViewer?: boolean;
    objectName?: string;
    fieldId?: string;
    hideTitle?: boolean;
};

export const PanelFieldSectionInline = ({
    title,
    children,
    isFocused = false,
    isRequired = false,
    dataType = '',
    error = '',
    disabled = false,
    isRecordField = false,
    centerContent = false,
    isDraggable = false,
    isDragging = false,
    showFieldMenu = true,
    rightHandPanelWidth,
    isRecordViewer,
    objectName,
    fieldId,
    hideTitle = false
}: PropsWithChildren<PanelFieldSectionInlineProps>) => {
    const theme = useTheme();
    const isAttachment = dataType === 'attachment' || isRecordField;
    const resizeField = rightHandPanelWidth && rightHandPanelWidth <= 350;

    const getLabelWidth = () => {
        if (resizeField) return '100%';
        if (isRecordViewer) return '45%';
        return '30%';
    };

    const getValueWidth = () => {
        if (resizeField) return '100%';
        if (isRecordViewer) return '55%';
        return '70%';
    };

    const backgroundColorHandler = () => {
        if (isAttachment) return theme.palette.background.paper;
        if (isFocused) return theme.palette.secondary.light;
        if (disabled) return theme.palette.primary[500];
        return theme.palette.primary[300];
    };

    const labelContentWidth = () => {
        if (isRecordViewer) {
            return {
                labelWidth: error ? '45%' : '70%',
                fieldMenuWidth: error ? '55%' : '30%'
            };
        }

        return {
            labelWidth: '80%',
            fieldMenuWidth: '20%'
        };
    };

    const fieldToMenu = objectName ? title : `${title}::${fieldId}`;

    return (
        <Box
            id="render-edit-field"
            sx={{
                width: '100%',
                mb: '0.5rem',
                opacity: isDragging ? 0.8 : 1,
                cursor: isDraggable ? 'grab' : 'default',
                '&:active': {
                    cursor: isDraggable ? 'grabbing' : 'default'
                }
            }}
        >
            <Box
                id="render-edit-field-grid"
                display="flex"
                height="100%"
                sx={{
                    flexDirection: resizeField ? 'column' : 'row',
                    alignItems: resizeField ? 'flex-start' : 'center',
                    gap: resizeField ? '0.5rem' : '0'
                }}
            >
                {!hideTitle && (
                    <Box
                        data-testid="render-edit-field-label"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width={getLabelWidth()}
                        sx={{ pr: '0.1rem' }}
                    >
                        <Box
                            display="flex"
                            alignItems={resizeField ? 'flex-start' : 'center'}
                            justifyContent={resizeField ? 'space-between' : 'flex-start'}
                            width={labelContentWidth().labelWidth}
                        >
                            {isDraggable && (
                                <DragIndicator
                                    sx={{
                                        fontSize: '1.5rem',
                                        color: theme.palette.secondary[800],
                                        cursor: 'grab',
                                        '&:active': {
                                            cursor: 'grabbing'
                                        }
                                    }}
                                />
                            )}
                            <Typography
                                title={title}
                                gutterBottom
                                sx={{ ...theme.typography.formLabel, whiteSpace: resizeField ? 'nowrap' : 'normal' }}
                            >
                                {formatLabel(title)}:
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                            width={labelContentWidth().fieldMenuWidth}
                        >
                            {showFieldMenu && <FieldMenu field={fieldToMenu} objectName={objectName} />}
                            {isRequired && !disabled && <span style={{ color: theme.palette.error.main, height: '0.875rem' }}>*</span>}
                            {error && !disabled && (
                                <Tooltip title={error} placement="top">
                                    <InfoOutlined sx={{ fontSize: '1rem', color: theme.palette.error.main, ml: '0.25rem' }} />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                )}
                <Box
                    data-testid="render-edit-field-value"
                    display="flex"
                    alignItems="center"
                    justifyContent={centerContent ? 'center' : 'flex-start'}
                    flexDirection={isAttachment ? 'column' : 'row'}
                    width={hideTitle ? '100%' : getValueWidth()}
                    sx={{
                        backgroundColor: backgroundColorHandler(),
                        borderRadius: '0.5rem',
                        px: centerContent || ['dropdown', 'single-select'].includes(dataType.toLowerCase()) ? 0 : '0.75rem',
                        minHeight: '2rem',
                        height: isAttachment ? 'auto' : '2rem',
                        border: error ? `1px solid ${theme.palette.error.main}` : 'none',
                        overflow: 'hidden'
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
};
